import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

/* Platform navigation (Shop / Memberships / Experiences ...) is driven by the
   "platformNavigation" key in the client admin panel configuration
   (client-configuration API, clientConfig list). Its value is JSON:
   {
     "enabled": 1,
     "placement": "above" | "below",          // relative to the gift card categories
     "titleKey": "explore_reserve", "title": "Explore Reserve",
     "comingSoonKey": "coming_soon",
     "items": [
       { "labelKey": "nav_shop", "label": "Shop", "url": "https://...",
         "openIn": "same" | "new", "status": "active" | "comingsoon" | "hidden",
         "displayOrder": 1, "visible": 1 }
     ]
   }
   Labels are looked up in Content Master (UIClient) by key, falling back to the
   plain text. A missing, disabled or malformed config renders nothing. */

const PLATFORM_NAV_CONFIG_KEY = "platformNavigation";
const TRUTHY_VALUES = ["1", "true", "active", "yes"];

const isTruthy = (value) =>
  value === true || TRUTHY_VALUES.includes(String(value ?? "").trim().toLowerCase());

// Visible defaults to true so an item only disappears when it is switched off.
const isVisible = (value) =>
  value === undefined || value === null || value === "" || isTruthy(value);

const normalise = (value) =>
  String(value ?? "").toLowerCase().replace(/[^a-z]/g, "");

// Only site-relative paths and http(s) links are allowed, so a bad config can
// never inject a javascript: or data: link.
const getLinkType = (url) => {
  const value = String(url ?? "").trim();
  if (value.startsWith("/") && !value.startsWith("//")) return "internal";
  if (/^https?:\/\//i.test(value)) return "external";
  return "";
};

const parseConfig = (rawValue) => {
  if (!rawValue) return null;
  try {
    const parsed = JSON.parse(rawValue);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : null;
  } catch {
    return null;
  }
};

const buildPlatformNavigation = (rawValue, translations) => {
  const translate = (resourceKey, fallback = "") => {
    if (!resourceKey || !Array.isArray(translations)) return fallback;
    const match = (clientOnly) =>
      translations.find(
        (item) =>
          (clientOnly ? item.clientId !== 0 : item.clientId === 0) &&
          item.resourceType === "UIClient" &&
          item.resourceKey === resourceKey
      )?.resourceValue;
    return match(true) || match(false) || fallback;
  };

  const config = parseConfig(rawValue);
  if (!config || !isTruthy(config.enabled) || !Array.isArray(config.items)) {
    return null;
  }

  const comingSoonText = translate(config.comingSoonKey || "coming_soon", "Coming Soon");

  const items = config.items
    .map((item, index) => {
      const status = normalise(item?.status) || "active";
      const linkType = getLinkType(item?.url);
      const label = translate(item?.labelKey, item?.label || "");
      return {
        key: `${item?.labelKey || item?.label || "item"}-${index}`,
        label,
        url: String(item?.url ?? "").trim(),
        linkType,
        newTab: normalise(item?.openIn) === "new" || normalise(item?.openIn) === "newtab",
        isComingSoon: status === "comingsoon",
        isHidden: status === "hidden" || !isVisible(item?.visible),
        isActive: status === "active",
        displayOrder: Number(item?.displayOrder ?? item?.order) || index + 1,
      };
    })
    // Active items need a valid link; coming soon items are shown without one.
    .filter(
      (item) =>
        item.label &&
        !item.isHidden &&
        ((item.isActive && item.linkType) || item.isComingSoon)
    )
    .sort((a, b) => a.displayOrder - b.displayOrder);

  if (items.length === 0) return null;

  return {
    title: translate(config.titleKey, config.title || ""),
    placement: normalise(config.placement) === "below" ? "below" : "above",
    comingSoonText,
    items,
  };
};

// Reads the raw value from the client-configuration API only. The admin
// panel's client master configurations are not used for this key.
const usePlatformNavConfigValue = () => {
  const clientConfig = useSelector(
    (state) => state.clientConfigurationReducer?.clientConfigurationData?.[0]?.clientConfig
  );
  if (!Array.isArray(clientConfig)) return "";
  const value = clientConfig.find(
    (item) =>
      String(item?.configKey ?? "").trim().toLowerCase() ===
      PLATFORM_NAV_CONFIG_KEY.toLowerCase()
  )?.configValue;
  return String(value ?? "").trim();
};

// Memoised so headers get a stable object until the config or translations change.
const usePlatformNavigation = () => {
  const rawValue = usePlatformNavConfigValue();
  const translations = useSelector((state) => state.translationReducer?.data);
  return useMemo(
    () => buildPlatformNavigation(rawValue, translations),
    [rawValue, translations]
  );
};

/* Renders one platform navigation item. Coming soon items are muted and not
   clickable; active items open their internal route or external link. */
const PlatformNavLink = ({ item, comingSoonText, className = "", onSelect }) => {
  const content = (
    <>
      <span className="platform-nav-label">
        {item.isComingSoon ? `${item.label} (${comingSoonText})` : item.label}
      </span>
      <svg
        className="platform-nav-arrow"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
      </svg>
    </>
  );

  if (item.isComingSoon) {
    return (
      <span className={`platform-nav-link is-coming-soon ${className}`} aria-disabled="true">
        {content}
      </span>
    );
  }

  const handleClick = () => onSelect?.(item);
  const linkClass = `platform-nav-link ${className}`;

  if (item.linkType === "internal" && !item.newTab) {
    return (
      <Link to={item.url} className={linkClass} onClick={handleClick}>
        {content}
      </Link>
    );
  }

  return (
    <a
      href={item.url}
      className={linkClass}
      onClick={handleClick}
      {...(item.newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {content}
    </a>
  );
};

export { usePlatformNavigation, PlatformNavLink };
